import { MM_PER_PT, PRINT_FONT_SIZE_PT } from '../constants';
import { ReportColumn, ReportPageSetup } from '../types';
import { resolvePrintableWidthMm } from './resolvePrintableWidthMm';

export type NormalizedColumnWidths = {
  /** Percentage width per column, in the same order as the input. */
  percents: number[];
  /** Width of the table relative to the printable area. */
  tablePercent: number;
  /** True when the requested em widths are wider than the printable area. */
  overflows: boolean;
  requestedMm: number;
  printableMm: number;
};

/**
 * Turns the template's relative em widths into percentages of the printable
 * width. Percentages (not mm) are what keeps a template valid across paper
 * formats: the same template prints correctly on A4 and A5, portrait and
 * landscape. `overflows` reports that the requested widths were wider than the
 * page, so the designer can warn instead of silently squeezing columns.
 */
export const normalizeColumnWidths = (
  columns: ReportColumn[],
  page: ReportPageSetup
): NormalizedColumnWidths => {
  const printableMm = resolvePrintableWidthMm(page);
  const widths = columns.map((column) =>
    typeof column.width === 'number' && column.width > 0 ? column.width : 1
  );
  const totalEm = widths.reduce((total, width) => total + width, 0);
  const requestedMm = totalEm * PRINT_FONT_SIZE_PT * MM_PER_PT;

  if (totalEm <= 0) {
    return {
      percents: [],
      tablePercent: 0,
      overflows: false,
      requestedMm: 0,
      printableMm,
    };
  }

  return {
    // Rounded: a raw division leaks float artefacts (27.500000000000004%) into
    // the rendered style attribute. Four decimals keep the columns exact enough
    // that the row still adds up to 100%.
    percents: widths.map(
      (width) => Math.round((width / totalEm) * 100 * 10000) / 10000
    ),
    tablePercent: Math.min(
      100,
      Math.round((requestedMm / printableMm) * 100 * 10000) / 10000
    ),
    overflows: requestedMm > printableMm,
    requestedMm,
    printableMm,
  };
};
